/**
 * # Curve
 *
 * A React component that renders a curved container with customizable depth, position, and padding level.
 *
 * ## Props
 * - `backgroundColor` (string): The background color of the curve.
 * - `className` (string): Additional CSS class name for the curve container.
 * - `depth` (string): The depth of the curve. Can be one of 'xl', 'l', 'm', 's', 'xs'. Default is 'm'.
 * - `paddingLevel` (number): The padding level of the curve. Default is 5.
 * - `position` (string): The position of the curve. Can be one of 'top', 'right', 'bottom', 'left'. Default is 'bottom'.
 * - `children` (node): The content to be rendered inside the curve container.
 *
 * ## Usage
 *
 * ```jsx
 * import Curve from './Curve.react';
 *
 * const MyComponent = () => {
 *   return (
 *     <Curve
 *       backgroundColor="#f0f0f0"
 *       className="my-curve"
 *       depth="l"
 *       paddingLevel={3}
 *       position="right"
 *     >
 *       <p>This is the content inside the curve.</p>
 *     </Curve>
 *   );
 * };
 * ```
 */

import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import makeConstants from 'lib/makeConstants';

import {paddingLevelPropType, paddingLevels} from '../../utils/paddingLevelProp';
import type {PaddingLevel} from '../../utils/paddingLevelProp';
import './curve.scss';

export const depthOptions = makeConstants('xl', 'l', 'm', 's', 'xs');
export const positionOptions = makeConstants('top', 'right', 'bottom', 'left');

type Depth = keyof typeof depthOptions;
type Position = keyof typeof positionOptions;

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  backgroundColor?: string;
  className?: string;
  depth?: Depth;
  paddingLevel?: PaddingLevel;
  position?: Position;
  children?: React.ReactNode;
}

const Curve = ({
  backgroundColor,
  children,
  className,
  depth = 'm',
  position = 'bottom',
  paddingLevel = 5,
  ...rest
}: Props) => {
  return (
    <div
      className={classnames(
        'a-curve',
        `a-curve--pos-${position}`,
        `a-curve--depth-${depth}`,
        paddingLevel != null && paddingLevels[paddingLevel],
        className
      )}
      {...rest}
    >
      <div
        className={classnames('a-curve__bg', `a-curve__bg--pos-${position}`)}
        style={backgroundColor ? {color: backgroundColor} : undefined}
      />
      {children}
    </div>
  );
};

Curve.propTypes = {
  backgroundColor: PropTypes.string,
  className: PropTypes.string,
  depth: PropTypes.oneOf(Object.values(depthOptions)),
  paddingLevel: paddingLevelPropType,
  position: PropTypes.oneOf(Object.values(positionOptions)),
  children: PropTypes.node
};

Curve.displayName = 'Curve';

export default Curve;
